import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRef, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../components/utils";
import {
  useAllocationMatrixStore,
  AllocationMatrixStatus,
} from "../../store/useAllocationMatrixStore";
import {
  useArchiveMatrix,
  useExportMatrix,
  useGetAllMatrices,
} from "../../queries/allocationMatrix";
import { downloadBlobObject } from "../../utils/DownloadFile";
import {
  useGetGrantLetterSettings,
  useSetGrantLetterSettings,
} from "../../queries/siteSettings";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import StatusLabel from "../esopOverview/StatusLabel";
import { CTADropdown } from "../../components/shared/Dropdown";

// ─── Global allocation-matrix settings card ───────────────────────────────────

function AllocationMatrixSettings({ matrices }: { matrices: any[] }) {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const { data: settings } = useGetGrantLetterSettings();
  const { mutate: saveSettings, isLoading: isSaving } =
    useSetGrantLetterSettings();

  const [enabled, setEnabled] = useState(false);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [matrixError, setMatrixError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.useAllocationMatrixForGranting ?? false);
      setDefaultId(settings.defaultAllocationMatrixId ?? null);
      setIsDirty(false);
    }
  }, [settings]);

  function handleToggle() {
    const next = !enabled;
    setEnabled(next);
    if (!next) {
      setDefaultId(null);
      setMatrixError(false);
    }
    setIsDirty(true);
  }

  function handleDefaultChange(id: string) {
    setDefaultId(id || null);
    setMatrixError(false);
    setIsDirty(true);
  }

  function handleSave() {
    if (enabled && !defaultId) {
      setMatrixError(true);
      return;
    }
    saveSettings(
      {
        useAllocationMatrixForGranting: enabled,
        defaultAllocationMatrixId: enabled ? defaultId : null,
      } as any,
      {
        onSuccess: () => {
          setIsDirty(false);
          toast("Settings saved.", { type: "success", autoClose: 2000 });
        },
        onError: () => {
          toast("Failed to save settings.", { type: "error", autoClose: 2000 });
        },
      }
    );
  }

  const activeMatrices = matrices.filter(
    (m) => m.status === AllocationMatrixStatus.ACTIVE
  );

  return (
    <VStack className="gap-6 px-5 py-5 mb-6 border rounded-lg">
      {/* Toggle row */}
      <HStack className="w-full justify-between items-center">
        <VStack className="flex-1">
          <span className="font-medium">
            Use Allocation Matrix for Option Issuance
          </span>
          <span className="text-xs text-gray-500">
            When enabled, option quantities for equity awards will be determined
            by the selected default matrix below.
          </span>
        </VStack>
        <SwitchButton
          value={enabled}
          onClick={handleToggle}
          disabled={isUserReadOnly || isSaving}
          label=""
        />
      </HStack>

      {/* Default matrix sub-row — only shown when feature is on */}
      {enabled && (
        <HStack className="w-full justify-between items-center gap-6">
          <VStack className="flex-1 pl-6 border-l-2 border-gray-100">
            <HStack className="items-center gap-1">
              <span className="font-medium">Default Allocation Matrix</span>
              <span className="text-red-500">*</span>
            </HStack>
            <span className="text-xs text-gray-500">
              The matrix applied by default when issuing options to employees.
              Only active matrices can be selected.
            </span>
          </VStack>
          <VStack className="items-end gap-1">
            <Select
              className={`w-52 font-medium text-sm ${
                matrixError ? "border-red-500" : ""
              }`}
              placeholder="Select a matrix"
              options={activeMatrices}
              value={defaultId ?? ""}
              onChange={(e) => handleDefaultChange(e.target.value)}
              textGetter={(m: any) => m.name}
              valueGetter={(m: any) => m.id}
              disabled={isUserReadOnly}
            />
            {matrixError && (
              <span className="text-xs text-red-500">
                A default matrix is required when this feature is enabled.
              </span>
            )}
          </VStack>
        </HStack>
      )}

      {/* Save CTA */}
      <HStack className="justify-end">
        <ButtonPrimary1
          onClick={handleSave}
          disabled={isUserReadOnly || isSaving || !isDirty}
        >
          {isSaving ? "Saving…" : "Save"}
        </ButtonPrimary1>
      </HStack>
    </VStack>
  );
}

function AllocationMatrix() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const gridApi = useRef<GridApi | null>(null);
  const navigate = useNavigate();
  const store = useAllocationMatrixStore();

  const { data: matrices = [] } = useGetAllMatrices();
  const { mutate: archiveMatrix } = useArchiveMatrix();
  const { mutate: exportMatrix } = useExportMatrix();

  function loadMatrixIntoStore(matrix: any) {
    store.clear();
    store.setMatrixId(matrix.id);
    store.setName(matrix.name ?? "");
    store.setDescription(matrix.description ?? "");
    store.setStatus(matrix.status);
    store.setNoMatchBehaviour(matrix.noMatchBehaviour);
    store.setRules(matrix.rules ?? []);
    store.setSelectedFactors(matrix.selectedFactors ?? []);
    store.setVersion(matrix.version ?? 1);
    store.setIsDefault(matrix.isDefault ?? false);
  }

  function handleAddAllocationMatrix() {
    store.clear();
    store.setIsReadOnly(false);
    navigate("/options/addAllocationMatrix");
  }

  function handleRowClick(matrix: any) {
    if (!matrix) return;
    loadMatrixIntoStore(matrix);
    store.setIsReadOnly(matrix.status !== AllocationMatrixStatus.DRAFT);
    navigate("/options/addAllocationMatrix");
  }

  function handleEdit(matrix: any) {
    loadMatrixIntoStore(matrix);
    store.setIsReadOnly(false);
    navigate("/options/addAllocationMatrix");
  }

  function handleExport(matrix: any) {
    exportMatrix(matrix.id, {
      onSuccess: (blob) => {
        downloadBlobObject(
          blob,
          `${(matrix.name ?? "allocation_matrix").replace(/\s+/g, "_")}.xlsx`
        );
      },
      onError: () => {
        toast("Failed to export matrix.", { type: "error", autoClose: 2000 });
      },
    });
  }

  function handleArchive(matrix: any) {
    archiveMatrix(matrix.id, {
      onSuccess: () => {
        toast("Allocation Matrix archived.", {
          type: "success",
          autoClose: 2000,
        });
      },
      onError: (err: any) => {
        toast(err?.response?.data?.reason || "Failed to archive matrix.", {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  }

  function handleAction(
    data: any,
    action: { name: string; disabled?: boolean }
  ) {
    if (action.disabled) return;
    if (action.name === "Edit") handleEdit(data);
    else if (action.name === "Archive") handleArchive(data);
    else if (action.name === "Export") handleExport(data);
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "NAME",
        field: "name",
        filter: "agTextColumnFilter",
        flex: 3,
        minWidth: 200,
        sortable: true,
        autoHeight: true,
        wrapText: true,
        cellRenderer: ({ data }: { data: any }) => (
          <VStack className="justify-center gap-0 py-1">
            <span className="font-medium">{data?.name}</span>
            {data?.description && (
              <span className="text-xs text-gray-light">
                {data.description}
              </span>
            )}
          </VStack>
        ),
      },
      {
        headerName: "STATUS",
        field: "status",
        filter: "agSetColumnFilter",
        sortable: true,
        cellStyle: { display: "flex", alignItems: "center" } as any,
        cellRenderer: ({ value }: { value: string }) => (
          <StatusLabel state={value ?? ""} />
        ),
      },
      {
        headerName: "FACTORS USED",
        field: "selectedFactors",
        sortable: true,
        autoHeight: true,
        valueGetter: ({ data }: { data: any }) =>
          Array.isArray(data?.selectedFactors)
            ? data.selectedFactors.length
            : 0,
      },
      {
        headerName: "RULES",
        field: "rules",
        sortable: true,
        autoHeight: true,
        valueGetter: ({ data }: { data: any }) =>
          Array.isArray(data?.rules) ? data.rules.length : 0,
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: false,
        sortable: false,
        menuTabs: ["columnsMenuTab"],
        cellRenderer: CTADropdown,
        cellRendererParams: (params: any) => {
          const isDraft = params.data?.status === AllocationMatrixStatus.DRAFT;
          const isArchived =
            params.data?.status === AllocationMatrixStatus.ARCHIVED;
          return {
            actions: [
              {
                name: "Edit",
                disabled: isUserReadOnly || isDraft,
                tooltipText: isDraft
                  ? "Click the row to edit a draft"
                  : undefined,
              },
              ...(!isArchived
                ? [
                    {
                      name: "Archive",
                      disabled: isUserReadOnly || isDraft,
                      tooltipText: isDraft
                        ? "Activate the matrix before archiving"
                        : undefined,
                    },
                  ]
                : []),
              { name: "Export", disabled: isUserReadOnly },
            ],
            onAction: (action: any) => handleAction(params.data, action),
          };
        },
      },
    ],
    [isUserReadOnly]
  );

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  return (
    <Box className="w-full p-4 bg-white rounded-lg">
      <HStack className="items-center justify-between mb-6">
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">
            Allocation Matrix
          </p>
          <p className="text-xs font-medium text-gray-light">
            {matrices.length} Allocation{" "}
            {matrices.length === 1 ? "Matrix" : "Matrices"}
          </p>
        </VStack>
        <ButtonPrimary
          onClick={handleAddAllocationMatrix}
          disabled={isUserReadOnly}
        >
          Add Allocation Matrix
        </ButtonPrimary>
      </HStack>

      <AllocationMatrixSettings matrices={matrices} />

      <HStack className="justify-between w-full">
        <Box
          style={{ height: "400px" }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            rowData={matrices}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform
            animateRows
            pagination
            rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            onCellClicked={(e) => {
              if (e.column.getColId() === "action-column") return;
              handleRowClick(e.data);
            }}
          />
        </Box>
      </HStack>
    </Box>
  );
}

export default AllocationMatrix;
